import { useEffect, useState } from "react";
import { Card,OverlayTrigger, Tooltip } from "react-bootstrap";
import { Address } from "../../../Physician/Models/Address";
import CCActivity from "../../../Physician/Models/CCActivity";
import { CCBlock, CCBlockRequirementType } from "../../../Physician/Models/CCBlock";
import { CCEnrollment } from "../../../Physician/Models/CCEnrollment";
import { MedicalLicense } from "../../../Physician/Models/MedicalLicense";
import AbpnSpinner from "../../../Shared/Components/AbpnSpinner";
import { StateWithCountryLookupItem } from "../../../Shared/Models/StateWithCountryLookupItem";
import { getDateWithOffset } from "../../../Shared/Utility/GetDateWithOffset";
import { AnnualFees } from "./AnnualFees";
import { CCActivityRequirements } from "./CCActivityRequirements";
import { MedicalLicensure } from "./MedicalLicensure";
import CCBlockEvaluationTernaryRequirement from "../../../Physician/Models/CCBlockEvaluationTernaryRequirement";
import Physician from "../../../Physician/Models/Physician";
interface IProps {
    ccEnrollment: CCEnrollment;
    ccBlock: CCBlock;
    contactAddress: Address;
    email: string;
    medicalLicenses: Array<MedicalLicense>;
    states: Array<StateWithCountryLookupItem>;
    onSaveBlock: (updatedBlock: CCBlock) => void;
    onSaveLicenses: (medicalLicenses: Array<MedicalLicense>) => void;
    hideLicenses: boolean; 
    isReadOnly?: boolean;
    showCollapse?: boolean;
    isSectionOpen?: boolean;
    showChangesText?: boolean;
    ccVerifiedActivities: Array<CCActivity>;
    physician: Physician;
    updatePhysician: (updatedPhysician: Physician) => void;
    isPhysicianPatientSafetyRequired ?: CCBlockEvaluationTernaryRequirement;
}

export default function CCBlockCard(props: IProps) {
    const [blockYears, setBlockYears] = useState<Array<number>>([]);
    
    const isCurrentBlock = props.ccBlock.ccBlockId === props.ccEnrollment.currentCCBlock.ccBlockId;
    const isActivitiesReadOnly = !isCurrentBlock || props.isReadOnly || props.ccBlock.hasAudit;
    const isFeesReadOnly = !isCurrentBlock || props.isReadOnly;
    const showErrors = getDateWithOffset(props.ccBlock.endDate) < new Date() && 
                        (props.ccBlock.extensionDate == null || getDateWithOffset(props.ccBlock.extensionDate) < new Date());

    const isNewestBlock = props.ccBlock.startYear == Math.max.apply(Math, props.ccEnrollment.ccBlocks.map(function(block: CCBlock) { return block.startYear; }))
    const isBlockInFuture = props.ccBlock.startYear > new Date().getFullYear();
    const outstandingRequirements = props.ccBlock.outstandingRequirements;
    const isMeetingLicenseRequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.MedicalLicense);
    const isMeetingFeeRequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.Fees);
    const isMeetingPatientSafetyRequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.PatientSafety);
    const isMeetingPIPRequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.PIP);
    const isMeetingCMERequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.SA ||
                                                                        _.requirementType === CCBlockRequirementType.CME);
    const isMeetingAuditRequirement = !outstandingRequirements?.some(_ => _.requirementType === CCBlockRequirementType.FailedAudit);
    const isMeetingActivityRequirements = isMeetingCMERequirement && isMeetingPIPRequirement && isMeetingPatientSafetyRequirement && isMeetingAuditRequirement;

    const showActivityRequirements = (isCurrentBlock && !showErrors) || 
                                     (isCurrentBlock && showErrors && !isMeetingActivityRequirements) ||
                                     (!isCurrentBlock && isMeetingActivityRequirements) ||
                                     isActivitiesReadOnly;

    const showFees = (isCurrentBlock && !showErrors) || 
                     (isCurrentBlock && showErrors && !isMeetingFeeRequirement) ||
                     (!isCurrentBlock && isMeetingFeeRequirement) ||
                     isFeesReadOnly;

    useEffect(() => {
        const determineBlockYears = () => {
            const newBlockYears = [];

            for (var year = props.ccBlock.startYear; year <= props.ccBlock.endYear; year++) {
                newBlockYears.push(year);
            }
            setBlockYears(newBlockYears);
        }
       
        determineBlockYears();
    }, [props.ccBlock])

    const getUnpaidYears = () => {
        return blockYears.filter(year => props.ccBlock.ccAnnualFees?.find(_ => _.year == year && !_.isPaid && !_.isWaived));
    }
    const getUnpaidTotal = () => {
        return props.ccBlock.ccAnnualFees.filter(_ => !_.isPaid && !_.isWaived).map(_ => _.feeAmount).reduce((a, b) => a + b, 0);
    }

    if (props.ccBlock == null) return <AbpnSpinner></AbpnSpinner>
    return (
  
        <Card className="mb-4" border="pp-secondary-grey" bg={ showErrors ? "pp-danger" : "pp-background"}>
            <Card.Body >
                <Card.Title className="mb-0 font-weight-bolder">                 
                    <span className="text-uppercase">
                        Continuing Certification Status {props.ccBlock.startYear} - {props.ccBlock.endYear}
                        {props.showCollapse && <i className="font-weight-bolder las la-angle-down float-right"></i>}
                    </span>                 
                    {props.ccBlock.hasFailedAudit && 
                    <span className="pp-div-CC-FailAuditBanner pp-title-card-right">You have not passed the CC audit of this block.
                    <OverlayTrigger
                        placement={"right"}
                        trigger="click"
                        rootClose={true}
                        overlay={<Tooltip id="failAuditBannerTooltip" bsPrefix="pp-tooltip">
                            You have not passed the CC audit of this block. Therefore, your board certification status has been updated to "Certified, Not Meeting CC Requirements". Communication of the audit status has been sent to the email on file in your Physician Portal account. This CC block may not be edited until this audit has been satisfied.<br/><br/>
                            Once you have documentation of all completed requirements, please send it via email to credentials@abpn.org and inform our office that you are contacting us to resolve the audit. If you need an additional PIP Audit Form, you can find that on the <a target="_blank" rel='noreferrer' href={"https://www.abpn.org/maintain-certification/abpn-diplomate-audits/"}>ABPN Diplomates Audits webpage</a>.<br/><br/>  
                            If you have any questions, please contact the ABPN office at credentials@abpn.org or call 847-229-6510.
                        </Tooltip>}>
                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                    </OverlayTrigger>
                    </span>
                    }
                     {props.ccBlock.hasPassedAudit && 
                     <span className="pp-div-CC-PassAuditBanner pp-title-card-right">You have passed the CC audit of this block.
                     <OverlayTrigger
                        placement={"right"}
                        trigger="click"
                        rootClose={true}
                        overlay={<Tooltip id="passAuditBannerTooltip" bsPrefix="pp-tooltip">
                            Congratulations! You have successfully passed this CC block audit. No other documentation is required at this time.<br/><br/>
                            If you have any questions, please contact the ABPN office at credentials@abpn.org or call 847-229-6510.
                        </Tooltip>}>
                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                    </OverlayTrigger>
                    </span>
                    }
                     {props.ccBlock.hasAudit && !props.ccBlock.hasPassedAudit && !props.ccBlock.hasFailedAudit && 
                     <span className="pp-div-CC-HasAuditBanner pp-title-card-right">You have been randomly selected for the CC audit of this block.
                     <OverlayTrigger
                        placement={"right"}
                        trigger="click"
                        rootClose={true}
                        overlay={<Tooltip id="hasAuditBannerTooltip" bsPrefix="pp-tooltip">
                           You have been randomly selected for an audit of this CC block. If you have not yet received email notification of the audit, please check your email spam folder or you should expect to get an email within the next few weeks. You may visit the <a target="_blank" rel='noreferrer' href={"https://www.abpn.org/maintain-certification/abpn-diplomate-audits/"}>ABPN Diplomate Audits webpage</a> to learn more about what to expect during the audit and what is required.<br/><br/>
                           The Self-Reported column within your "continuing certification activity requirements" section of the Physician Portal may not be edited for the remainder of this CC block. However, verified and waived activity credits will continue to be updated, and you may still view and manage your activity records and view the ABPN approved activities list.<br/><br/>
                           If you have any questions, please contact the ABPN office at credentials@abpn.org or call 847-229-6510.
                        </Tooltip>}>
                        <i className="las la-info-circle ml-1 text-pp-secondary"></i>
                    </OverlayTrigger>
                    </span>
                    }
                    <br/>
                    {props.ccBlock.hasAudit && !props.ccBlock.hasPassedAudit && !props.ccBlock.hasFailedAudit && <span><br/></span>}
                </Card.Title>
                {showErrors && <h5 className="font-weight-bolder mt-2">Not Meeting Requirements</h5>}

                    <div>
                        {showErrors &&
                            <div>
                                <ul className="pp-unordered-list">
                                    <li className="pp-font-size-smaller" hidden={isMeetingCMERequirement}>Attest to 90 CME, Including 24 SA-CME</li>
                                    <li className="pp-font-size-smaller" hidden={isMeetingPIPRequirement}>Attest to Improvement In Medical Practice (PIP)</li>
                                    <li className="pp-font-size-smaller" hidden={isMeetingPatientSafetyRequirement}>Attest to Patient Safety</li>
                                    <li className="pp-font-size-smaller" hidden={isMeetingFeeRequirement}>Pay ({getUnpaidYears().join(", ")}) annual fee (${getUnpaidTotal()})</li>
                                    <li className="pp-font-size-smaller" hidden={isMeetingLicenseRequirement}>An unrestricted, non-expired Medical License is required</li>
                                </ul>
                            </div>
                        }
                
                        {isBlockInFuture ? 
                            <div><span>Requirements for the {props.ccBlock.startYear} - {props.ccBlock.endYear} block will be available in early January of {props.ccBlock.startYear}</span></div> : 
                            <div className="mt-4">
                            {
                                showActivityRequirements &&
                                <CCActivityRequirements
                                    ccBlock={props.ccBlock}
                                    ccVerifiedActivities={props.ccVerifiedActivities}
                                    isReadOnly={isActivitiesReadOnly } 
                                    physician={props.physician}
                                    updatePhysician={props.updatePhysician}    
                                    onSaveBlock={props.onSaveBlock} 
                                    isPhysicianPatientSafetyRequired={props.isPhysicianPatientSafetyRequired}/>
                            }
                            {
                                showFees &&
                                <AnnualFees
                                    email={props.email}
                                    contactAddress={props.contactAddress}
                                    ccBlock={props.ccBlock}
                                    onSaveBlock={props.onSaveBlock}
                                    isReadOnly={isFeesReadOnly} 
                                    blockYears={blockYears} />
                            }
                            {
                                !props.hideLicenses &&
                                <MedicalLicensure
                                    medicalLicenses={props.medicalLicenses}
                                    states={props.states}
                                    onSaveLicenses={props.onSaveLicenses}
                                    isSectionOpen={true} 
                                    showCollapse={false}/>
                            }
                        </div>
                        }
                        
                    </div>
            </Card.Body>
        </Card>
        
       
    );
}